import React, { useEffect, useMemo, useState } from "react";

import Icon from "../Icon";
import Modal from "../Modal";
import LinearProgressBar from "../ProgressBar/LinearProgressBar";
import "./style.scss";

const AUTO_MODES = new Set(["auto", "auto-single", "auto-bulk"]);

const AUTO_LOADING_STAGES = [
  { stage: "Initializing recommendation engine...", progress: 10, delay: 0 },
  {
    stage: "Analyzing executive profiles and performance metrics...",
    progress: 40,
    delay: 800,
  },
  {
    stage: "Calculating compatibility scores...",
    progress: 75,
    delay: 1800,
  },
  { stage: "Finalizing recommendations...", progress: 75, delay: 2600 },
];

const MANUAL_STAGE = "Loading available sponsors...";
const MANUAL_LOADING_STAGES = [
  { stage: MANUAL_STAGE, progress: 10, delay: 0 },
  { stage: MANUAL_STAGE, progress: 40, delay: 800 },
  { stage: MANUAL_STAGE, progress: 75, delay: 1800 },
  { stage: MANUAL_STAGE, progress: 75, delay: 2600 },
];

const TITLES = {
  auto: "Getting AI Recommendations",
  manual: "Loading all available executives",
} as const;

const noop = () => null;

interface RecommendationsLoaderProps {
  isOpen: boolean;
  mode: "single" | "bulk";
  assignmentMode?: string;
  currentAccountName?: string;
  currentAccountIndex?: number;
  totalAccounts?: number;
}

const RecommendationsLoader: React.FC<RecommendationsLoaderProps> = ({
  isOpen,
  mode,
  assignmentMode,
  currentAccountName,
  totalAccounts = 1,
}) => {
  const [progress, setProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState("");

  const isAutoMode = AUTO_MODES.has(assignmentMode ?? "");

  const loadingStages = isAutoMode
    ? AUTO_LOADING_STAGES
    : MANUAL_LOADING_STAGES;

  const simulatedAccountIndex = useMemo(() => {
    if (mode === "bulk" && totalAccounts > 1 && progress > 0) {
      const accountProgress = Math.floor((progress / 100) * totalAccounts);
      return Math.min(accountProgress, totalAccounts - 1);
    }
    return 0;
  }, [progress, mode, totalAccounts]);

  // Simulate progress with loading stages
  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setLoadingStage("");
      return;
    }

    // Initialize first stage
    setLoadingStage(loadingStages[0].stage);
    setProgress(loadingStages[0].progress);

    // Set up stage transitions
    const timeouts: NodeJS.Timeout[] = [];

    loadingStages
      .slice(1)
      .forEach(({ stage, progress: targetProgress, delay }) => {
        const timeout = setTimeout(() => {
          setLoadingStage(stage);
          setProgress(prev => Math.max(prev, targetProgress));
        }, delay);
        timeouts.push(timeout);
      });

    // Continue progress animation after reaching 75%
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        if (prev >= 75) {
          const increment = mode === "bulk" ? 1.5 : 2;
          return Math.min(prev + increment, 100);
        }
        return prev;
      });
    }, 100);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      clearInterval(interval);
    };
  }, [isOpen, mode, loadingStages]);

  const subtitle = useMemo(() => {
    if (!isAutoMode) return MANUAL_STAGE;

    const displayIndex = simulatedAccountIndex + 1;
    const isBulk = mode === "bulk" && totalAccounts > 1;
    const accountPart = currentAccountName ? `: ${currentAccountName}` : "";

    if (!loadingStage) {
      return isBulk
        ? `Processing account ${displayIndex} of ${totalAccounts}${accountPart}`
        : currentAccountName
          ? `Processing: ${currentAccountName}`
          : "Processing recommendations...";
    }

    return isBulk
      ? `${loadingStage} (Account ${displayIndex} of ${totalAccounts}${accountPart})`
      : loadingStage;
  }, [
    loadingStage,
    mode,
    totalAccounts,
    simulatedAccountIndex,
    currentAccountName,
    isAutoMode,
  ]);

  return (
    <Modal isOpen={isOpen} toggle={noop} showHeader={false} size="md" centered>
      <div className="recommendations-loader">
        <div className="recommendations-loader__header flex items-center justify-end">
          <div className="recommendations-loader__icon">
            <Icon
              name="processing"
              style={{ color: "#974ae4", width: 24, height: 24 }}
            />
          </div>
        </div>

        <div className="recommendations-loader__content">
          <h3 className="recommendations-loader__title">
            {TITLES[isAutoMode ? "auto" : "manual"]}
          </h3>
          <p className="recommendations-loader__subtitle">{subtitle}</p>

          <div className="recommendations-loader__progress-section">
            <p className="recommendations-loader__progress-label">Progress</p>
            <LinearProgressBar percentage={Math.round(progress)} />
          </div>

          <div className="recommendations-loader__footer">
            <span className="recommendations-loader__dots">
              • Processing • Please wait •
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RecommendationsLoader;
